<template>
    <HeaderCardsLayout variant="No-Logo">
        <template #headline>
            {{ $t('users') }} <span v-if="userCount">({{ userCount }})</span>
        </template>
        <template #icon>
            <UsersIcon class="w-12 h-12" />
        </template>
        <template #buttons>
            <LinkButton
                variant="Neutral-Light"
                size="Base"
                :disabled-explanation="createDevAccountDisabled"
                :to="{ name: 'user-create' }"
            >
                <template #icon>
                    <PlusIcon class="size-5" />
                </template>
                {{ $t('create-dev-account') }}
            </LinkButton>
        </template>
        <template #body>
            <PaddedContent>
                <div v-if="loading">
                    <Message status="info">{{ $t('loading-users') }}</Message>
                </div>
                <div v-if="!loading && !users">
                    <EmptyState>
                        <template #description>
                            {{ $t('no-users') }}
                        </template>
                    </EmptyState>
                </div>
                <div v-if="!loading && users" class="space-y-2">
                    <UserCard
                        v-for="user in users"
                        :key="user.id"
                        :first-name="user.firstName"
                        :last-name="user.lastName"
                        :activation-status="user.activationStatus"
                        :avatar-url="user.avatarUrl"
                        :link-target="{ name: 'user-details', params: { userId: user.id } }"
                    >
                        <template #loginmethods>
                            <LoginMethodBadge
                                v-if="user.passwordLogin && user.passwordLogin.isActive"
                                variant="Sky"
                                :tooltip-text="$t('login-dev-tooltip-text')"
                            >
                                <CodeIcon class="size-5" />
                            </LoginMethodBadge>

                            <LoginMethodBadge
                                v-for="sso in user.ssoLogins.filter((s) => s.isActive)"
                                :key="sso.provider"
                                :variant="sso.badgeVariant"
                                :tooltip-text="sso.tooltipText"
                            >
                                <SSOIcon class="size-5" />
                            </LoginMethodBadge>
                        </template>
                        <template #permissions>
                            <Badge v-if="user.isAdmin" variant="Sky" size="Small">
                                <template #icon>
                                    <EditIcon class="size-4.5" />
                                </template>
                                {{ $t('permissions-write') }}
                            </Badge>
                        </template>
                        <template #buttons>
                            <Button
                                v-for="inactiveLogin in user.inactiveSsoLogins"
                                :key="inactiveLogin.id"
                                size="Small"
                                variant="Neutral-Strong"
                                :status="activationButtonStatus.get(user.id) ?? 'Default'"
                                :disabled-explanation="activateSsoLoginDisabledExplanation"
                                @click="activateSsoLogin(inactiveLogin, user.id)"
                            >
                                <template #icon>
                                    <ModeOnIcon class="size-4.5" />
                                </template>
                                {{ $t('activate-sso-login', { provider: inactiveLogin.provider }) }}
                            </Button>
                            <template v-if="user.activationStatus === 'Active'">
                                <LinkButton
                                    size="Small"
                                    variant="Neutral-Light"
                                    :to="{ name: 'user-edit', params: { userId: user.id } }"
                                    :disabled-explanation="userEditDisabledExplanation"
                                >
                                    <template #icon>
                                        <EditIcon class="size-4.5" />
                                    </template>
                                    {{ $t('edit') }}
                                </LinkButton>
                                <LinkButton
                                    size="Small"
                                    variant="Danger-Light"
                                    :icon-description="$t('deactivate-preview')"
                                    :to="{ name: 'user-deactivate', params: { userId: user.id } }"
                                    :disabled-explanation="deactivatePreviewDisabledExplanation"
                                >
                                    <template #icon>
                                        <BlockIcon class="size-4.5" />
                                    </template>
                                </LinkButton>
                            </template>
                            <template v-if="user.activationStatus === 'Inactive'">
                                <LinkButton
                                    size="Small"
                                    variant="Neutral-Strong"
                                    :disabled-explanation="userActivateDisabledExplanation"
                                    :to="{ name: 'user-activate', query: { oid: user.id, provider: user.provider } }"
                                >
                                    <template #icon>
                                        <ModeOnIcon class="size-4.5" />
                                    </template>
                                    {{ $t('activate-user-preview') }}
                                </LinkButton>
                            </template>
                            <template v-if="user.activationStatus === 'Deactivated'">
                                <LinkButton
                                    size="Small"
                                    variant="Neutral-Light"
                                    :disabled-explanation="userReactivateDisabledExplanation"
                                    :to="{ name: 'user-edit', params: { userId: user.id } }"
                                >
                                    <template #icon>
                                        <RefreshIcon class="size-4.5" />
                                    </template>
                                    {{ $t('reactivate-user') }}
                                </LinkButton>
                            </template>
                        </template>
                    </UserCard>
                </div>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import Badge from '@/components/Badge/Badge.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
import BlockIcon from '@/components/Icon/BlockIcon.vue';
import CodeIcon from '@/components/Icon/CodeIcon.vue';
import EditIcon from '@/components/Icon/EditIcon.vue';
import ModeOnIcon from '@/components/Icon/ModeOnIcon.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import RefreshIcon from '@/components/Icon/RefreshIcon.vue';
import SSOIcon from '@/components/Icon/SSOIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import LoginMethodBadge from '@/components/LoginMethodBadge/LoginMethodBadge.vue';
import Message from '@/components/Message/Message.vue';
import UserCard from '@/components/UserCard/UserCard.vue';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { computed } from 'vue';
import { useActivateSsoLogin } from '../composables/useActivateSsoLogin';
import { useUserList } from '../composables/useUserList';

const { users, userCount, loading, updateUsers } = useUserList();

const createDevAccountDisabled = computed(() => (auth.isAdmin() ? undefined : $t('no-permission-to-create')));
const deactivatePreviewDisabledExplanation = computed(() =>
    auth.isAdmin() ? undefined : $t('no-permission-to-deactivate'),
);
const userEditDisabledExplanation = computed(() => (auth.isAdmin() ? undefined : $t('no-permission-to-edit')));
const userReactivateDisabledExplanation = computed(() =>
    auth.isAdmin() ? $t('not-implemented') : $t('no-permission-to-reactivate'),
);
const activateSsoLoginDisabledExplanation = computed(() =>
    auth.isAdmin() ? undefined : $t('no-permission-to-activate'),
);
const userActivateDisabledExplanation = computed(() => (auth.isAdmin() ? undefined : $t('no-permission-to-activate')));

const { activationButtonStatus, activateSsoLogin } = useActivateSsoLogin(users, updateUsers);

useHeadSafe({ title: () => `${$t('user-overview')} - ${$t('users')}` });
</script>
